import { useQuery } from "@tanstack/vue-query"
import type { Subject } from "~/types/subject"
import type { Folder } from "~/types/folder"
import { ContentType } from "~/models/Content/ContentType"
import * as v from "valibot"
import { ProductContributorSchema } from "~/models/Content/schemas"
import type { ProductContributor } from "~/models/Content/schemas"

export function useSubjectsAndFolders(locationId: MaybeRef<number | undefined>) {
  const getContent = useGetContent()

  return useQuery({
    queryKey: ["content", "subjects-and-folders", locationId] as const,
    staleTime: 1000 * 60 * 60 * 6, // 6 hours
    enabled: computed(() => !!toValue(locationId)),
    queryFn({ queryKey }) {
      if (typeof queryKey[2] !== "number") {
        return []
      }

      return getContent({
        childDepth: 1,
        parentLocationId: [queryKey[2]],
        contentType: [ContentType.Folder, ContentType.Chapter],
        sortField: "priority",
        sortOrder: "asc",
      })
    },
    select(items) {
      const folders: Folder[] = []
      let subjects: Subject[] = []

      for (const item of items) {
        if (isContentType(ContentType.Folder)(item)) {
          folders.push(item)

          for (const child of (item.children ?? [])) {
            if (isContentType(ContentType.Chapter)(child)) {
              subjects.push(child)
            }
          }
        } else if (isContentType(ContentType.Chapter)(item)) {
          subjects.push(item)
        }
      }

      if (folders.length <= 0) {
        subjects.sort(byPriority)
      } else {
        folders.sort(byPriority)

        // If there are folders, we don't want top-level subjects.
        const subjectsContainedByFolders: Subject[] = []
        for (const folder of folders) {
          subjectsContainedByFolders.push(
            ...subjects
              .filter(subject => subject.parentLocationId === folder.id)
              .sort(byPriority),
          )
        }

        subjects = subjectsContainedByFolders
      }

      return {
        folders,
        subjects: subjects.map(subject => {
          const contributors = (subject.relatedItems ?? []).filter(r => v.is(ProductContributorSchema, r))
          const crediting: ProductContributor[] = []

          for (const creditingId of (subject.creditingIds ?? [])) {
            const contributorMatch = contributors.find(c => c.contentId === creditingId)

            if (contributorMatch) {
              crediting.push(contributorMatch)
            }
          }

          return ({
            ...subject,
            crediting,
            introPages: subject.relatedItems
              ?.filter(isContentType(ContentType.ChapterPage))
              .map(page => ({
                ...page,
                video: page.relatedItems?.find(isContentType(ContentType.Video)),
              }))
              .sort((a, b) => {
                const indexA = subject.relationsIds.indexOf(a.contentId)
                const indexB = subject.relationsIds.indexOf(b.contentId)
                return indexA - indexB
              }),
          })
        }),
      }
    },
  })
}
